// controllers/subscriptionController.js  (frontend)
import apiService from '../services/apiService';
import { authController } from './authController';

const DEFAULT_SECTIONS = ['dashboard', 'help', 'settings', 'subscriptions'];

const ALL_SECTIONS = [
  ...DEFAULT_SECTIONS,
  'hospital', 'crm', 'hrms', 'projects', 'mobile-device',
  'white-label', 'translation', 'wallet', 'orders',
  'inventory', 'sales', 'reports', 'enquiry', 'mdm',
  'communication', 'users-management',
];

// ─── Internal helpers — same pattern as Sidebar ───────────────────────────────

/** Get current user via authController (same source as Sidebar) */
const _getUser = () => authController.getCurrentUser();

// Normalize role string — trims whitespace + lowercases, so "Admin", " admin ",
// "ADMIN" etc. all compare equal, no matter what casing the backend sends.
const _normalizeRole = (role) => (typeof role === 'string' ? role.trim().toLowerCase() : null);

/** Get role (normalized) */
const _getRole = () => _normalizeRole(_getUser()?.role);

/** Get userId — supports id and _id */
const _getUid  = () => { const u = _getUser(); return u?.id || u?._id || null; };

/**
 * Strict role guard — returns { blocked, reason } or { blocked: false }.
 * Use only for routes that must stay limited to specific roles
 * (currently: super_admin-only plan management / stats / init).
 */
const _guard = (allowedRoles) => {
  const role = _getRole();
  const normalizedAllowed = allowedRoles.map(_normalizeRole);

  if (!role) return { blocked: true, reason: 'Not authenticated. Please log in.' };

  if (!normalizedAllowed.includes(role)) {
    return { blocked: true, reason: `Access denied. Requires role: ${allowedRoles.join(' or ')}` };
  }

  return { blocked: false };
};

// Auth-only guard — allows ANY logged-in role (admin, retailer, whatever else exists).
// Used for functions that should be open to all roles, not just admin/super_admin.
// Blocks only if there is no authenticated user at all.
const _guardAuthenticated = () => {
  const role = _getRole();
  if (!role) return { blocked: true, reason: 'Not authenticated. Please log in.' };
  return { blocked: false };
};

// ─── Main controller ──────────────────────────────────────────────────────────
const subscriptionController = {

  // ── Auth helpers (exposed so SubscriptionManagement can use same source) ──

  /** Returns current user from authController — same as Sidebar */
  getCurrentUser: () => _getUser(),

  /** Returns current role (normalized) */
  getCurrentRole: () => _getRole(),

  /** true if super_admin — same check Sidebar uses */
  isSuperAdmin: () => _getRole() === 'super_admin',

  /** true if admin or super_admin */
  isAdmin: () => ['admin', 'super_admin'].includes(_getRole()),

  /**
   * Local (synchronous) access check — mirrors Sidebar's hasAccess() exactly:
   *   super_admin  → always true
   *   default sec  → always true
   *   no sub reqd  → true
   *   else         → check subscribedSections array
   */
  hasAccess: (sectionId, requiresSubscription, subscribedSections = []) => {
    const role = _getRole();
    if (role === 'super_admin')                    return true;
    if (DEFAULT_SECTIONS.includes(sectionId))      return true;
    if (!requiresSubscription)                     return true;
    return subscribedSections.includes(sectionId);
  },

  // ── Plans — Read (all authenticated roles) ─────────────────────────────────

  /** GET /api/subscriptions/plans */
  getAvailablePlans: async () => {
    const g = _guardAuthenticated();
    if (g.blocked) return { success: false, data: [], message: g.reason };
    try {
      const res = await apiService.get('subscriptions/plans');
      return { success: true, data: res.data || [] };
    } catch (e) { return { success: false, data: [], message: e.message || 'Failed to fetch plans' }; }
  },

  /** GET /api/subscriptions/plans/bundles */
  getBundlePlans: async () => {
    const g = _guardAuthenticated();
    if (g.blocked) return { success: false, data: [], message: g.reason };
    try {
      const res = await apiService.get('subscriptions/plans/bundles');
      return { success: true, data: res.data || [] };
    } catch (e) { return { success: false, data: [], message: e.message || 'Failed to fetch bundle plans' }; }
  },

  /** GET /api/subscriptions/plans/enterprise */
  getEnterprisePlan: async () => {
    const g = _guardAuthenticated();
    if (g.blocked) return { success: false, data: null, message: g.reason };
    try {
      const res = await apiService.get('subscriptions/plans/enterprise');
      return { success: true, data: res.data };
    } catch (e) { return { success: false, data: null, message: e.message || 'Failed to fetch enterprise plan' }; }
  },

  /** GET /api/subscriptions/plans/:planId */
  getPlanById: async (planId) => {
    const g = _guardAuthenticated();
    if (g.blocked) return { success: false, data: null, message: g.reason };
    try {
      const res = await apiService.get(`subscriptions/plans/${planId}`);
      return { success: true, data: res.data };
    } catch (e) { return { success: false, data: null, message: e.message || 'Failed to fetch plan' }; }
  },

  // ── Plans — Write (super_admin ONLY) ──────────────────────────────────────

  /**
   * POST /api/subscriptions/plans — create new plan
   * SUPER_ADMIN ONLY
   */
  createPlan: async (planData) => {
    const g = _guard(['super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post('subscriptions/plans', planData);
      return { success: true, data: res.data, message: 'Plan created successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to create plan' }; }
  },

  /**
   * PUT /api/subscriptions/plans/:planId — update plan
   * SUPER_ADMIN ONLY
   */
  updatePlan: async (planId, updates) => {
    const g = _guard(['super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.put(`subscriptions/plans/${planId}`, updates);
      return { success: true, data: res.data, message: 'Plan updated successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to update plan' }; }
  },

  /**
   * Toggle isActive for a plan
   * SUPER_ADMIN ONLY
   */
  togglePlanActive: async (planId, isActive) => {
    const g = _guard(['super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.put(`subscriptions/plans/${planId}`, { isActive });
      return {
        success: true, data: res.data,
        message: isActive ? 'Plan activated successfully' : 'Plan deactivated successfully',
      };
    } catch (e) { return { success: false, message: e.message || 'Failed to toggle plan status' }; }
  },

  /**
   * DELETE /api/subscriptions/plans/:planId — soft delete (sets isActive: false)
   * SUPER_ADMIN ONLY
   */
  deletePlan: async (planId) => {
    const g = _guard(['super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      await apiService.delete(`subscriptions/plans/${planId}`);
      return { success: true, message: 'Plan deactivated successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to delete plan' }; }
  },

  // ── User Subscriptions ────────────────────────────────────────────────────

  /**
   * GET subscribed sections for a user
   * super_admin → returns ALL_SECTIONS immediately (no API call) — same as Sidebar
   * everyone else → fetches from API
   */
  getSubscribedSections: async (userId) => {
    const role = _getRole();
    // super_admin gets everything without any API call — mirrors Sidebar exactly
    if (role === 'super_admin') {
      return { success: true, data: ALL_SECTIONS };
    }
    const g = _guardAuthenticated();
    if (g.blocked) return { success: false, data: DEFAULT_SECTIONS, message: g.reason };
    try {
      const res = await apiService.get(`subscriptions/sections/${userId}`);
      const subs = res.data || [];
      return { success: true, data: [...new Set([...DEFAULT_SECTIONS, ...subs])] };
    } catch (e) {
      return { success: false, data: DEFAULT_SECTIONS, message: e.message || 'Failed to fetch subscribed sections' };
    }
  },

  /** GET /api/subscriptions/details/:userId */
  getSubscriptionDetails: async (userId) => {
    const g = _guardAuthenticated();
    if (g.blocked) return { success: false, data: { totalSubscriptions: 0, subscriptions: [], costs: {} }, message: g.reason };
    try {
      const res = await apiService.get(`subscriptions/details/${userId}`);
      return { success: true, data: res.data };
    } catch (e) { return { success: false, data: { totalSubscriptions: 0, subscriptions: [], costs: {} }, message: e.message }; }
  },

  /** GET /api/subscriptions/cost/:userId?billingCycle=monthly */
  calculateTotal: async (userId, billingCycle = 'monthly') => {
    const g = _guardAuthenticated();
    if (g.blocked) return { success: false, data: 0, message: g.reason };
    try {
      const res = await apiService.get(`subscriptions/cost/${userId}?billingCycle=${billingCycle}`);
      return { success: true, data: res.data?.total ?? 0 };
    } catch (e) { return { success: false, data: 0, message: e.message }; }
  },

  // ── Subscribe / Unsubscribe (all authenticated roles) ───────────────────────

  /** POST /api/subscriptions/sections/:userId */
 subscribe: async (userId, sectionId, billingCycle = 'monthly', invoiceId = null) => {
  const g = _guardAuthenticated();
  if (g.blocked) return { success: false, message: g.reason };
  try {
    const res = await apiService.post(`subscriptions/sections/${userId}`, { sectionId, billingCycle, invoiceId });
    return { success: true, data: res.data, message: `Subscribed to ${sectionId}` };
  } catch (e) { return { success: false, message: e.message || 'Failed to subscribe' }; }
},

  /** DELETE /api/subscriptions/sections/:userId/:sectionId */
  unsubscribe: async (userId, sectionId) => {
    const g = _guardAuthenticated();
    if (g.blocked) return { success: false, message: g.reason };
    if (DEFAULT_SECTIONS.includes(sectionId)) {
      return { success: false, message: 'Cannot unsubscribe from default sections' };
    }
    try {
      await apiService.delete(`subscriptions/sections/${userId}/${sectionId}`);
      return { success: true, message: `Unsubscribed from ${sectionId}` };
    } catch (e) { return { success: false, message: e.message || 'Failed to unsubscribe' }; }
  },

  /** POST /api/subscriptions/sections/:userId/bulk */
bulkSubscribe: async (userId, sectionIds, billingCycle = 'monthly', invoiceId = null) => {
  const g = _guardAuthenticated();
  if (g.blocked) return { success: false, message: g.reason };
  try {
    const res = await apiService.post(`subscriptions/sections/${userId}/bulk`, { sectionIds, billingCycle, invoiceId });
    return { success: true, data: res.data, message: `Subscribed to ${sectionIds.length} sections` };
  } catch (e) { return { success: false, message: e.message || 'Failed to bulk subscribe' }; }
},

  /** POST /api/subscriptions/sections/:userId/bulk-delete */
  bulkUnsubscribe: async (userId, sectionIds) => {
    const g = _guardAuthenticated();
    if (g.blocked) return { success: false, message: g.reason };
    const filtered = sectionIds.filter(id => !DEFAULT_SECTIONS.includes(id));
    try {
      await apiService.post(`subscriptions/sections/${userId}/bulk-delete`, { sectionIds: filtered });
      return { success: true, message: `Unsubscribed from ${filtered.length} sections` };
    } catch (e) { return { success: false, message: e.message || 'Failed to bulk unsubscribe' }; }
  },

  // ── Checkout & Cancel ─────────────────────────────────────────────────────

  /** POST /api/subscriptions/checkout */
  checkout: async (userId, planIds, billingCycle = 'monthly') => {
    const g = _guardAuthenticated();
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post('subscriptions/checkout', {
        userId,
        planIds: Array.isArray(planIds) ? planIds : [planIds],
        billingCycle,
      });
      return { success: true, data: res.data, message: 'Checkout successful' };
    } catch (e) { return { success: false, message: e.message || 'Checkout failed' }; }
  },

  /** POST /api/subscriptions/cancel */
  cancelPlan: async (userId, planIds) => {
    const g = _guardAuthenticated();
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post('subscriptions/cancel', {
        userId,
        planIds: Array.isArray(planIds) ? planIds : [planIds],
      });
      return { success: true, data: res.data, message: 'Subscription cancelled successfully' };
    } catch (e) { return { success: false, message: e.message || 'Cancellation failed' }; }
  },

  // ── Statistics (super_admin only) ─────────────────────────────────────────

  /** GET /api/subscriptions/statistics */
  getStatistics: async (filters = {}) => {
    const g = _guard(['super_admin']);
    if (g.blocked) return { success: false, data: null, message: g.reason };
    try {
      const qs = new URLSearchParams(filters).toString();
      const res = await apiService.get(qs ? `subscriptions/statistics?${qs}` : 'subscriptions/statistics');
      return { success: true, data: res.data };
    } catch (e) { return { success: false, data: null, message: e.message }; }
  },

  // ── Seed / Init (super_admin only) ────────────────────────────────────────

  /** POST /api/subscriptions/init — upsert all default plans */
  initSubscriptions: async () => {
    const g = _guard(['super_admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post('subscriptions/init');
      return { success: true, data: res.data, message: res.data?.message || 'Initialized successfully' };
    } catch (e) { return { success: false, message: e.message || 'Init failed' }; }
  },
};

export default subscriptionController;